// src/services/socialAuthService.js
// 54차: 카카오 / 네이버 소셜 로그인 (Firebase Custom Token 방식)
//   흐름: 네이티브 SDK 로 access token 획득 → CF(kakaoLoginCF/naverLoginCF)로 검증·교환
//        → signInWithCustomToken 으로 Firebase 로그인 → ensureSocialUserDoc 로 신규가입 처리
//   ⚠️ 토큰 검증은 서버(CF)에서 수행. 클라는 access token 전달만.
//   SDK: @react-native-seoul/kakao-login, @react-native-seoul/naver-login
//        (네이티브 모듈 — prebuild + EAS 빌드 필요. Expo Go 동작 안 함)
// 74차: ensureSocialUserDoc 호출을 이 파일에서 제거 — Google/Apple(LoginScreen.js)과 동일하게
//   "인증"과 "프로필 생성"을 분리해서, 로그인 화면이 신규/기존 여부를 먼저 판별한 뒤 신규만
//   SocialOnboardingScreen에서 약관 동의를 받고 나서 ensureSocialUserDoc을 호출하도록 함.
//   agreedAt 파라미터도 제거 — 더 이상 이 함수 호출 시점에 동의를 받지 않음.
// 80차 계속: 웹(unlockapp.co.kr)도 일반 고객용 데모로 홍보하게 되면서 SNS 로그인이
//   웹에서 전부 막혀있던 것("지원 안 함")을 우선순위 높은 카카오/네이버부터 열기로 함.
//   kakaoLoginCF/naverLoginCF는 access token만 받아서 카카오/네이버 서버에 직접 검증하는
//   구조라 그 토큰을 "어떻게" 얻었는지(네이티브 SDK vs 웹)는 서버 입장에서 전혀 중요하지
//   않음 — 그래서 "웹에서 로그인을 완결짓는 방법"만 새로 추가하면 됨. 둘 다 팝업을 직접
//   열고 리다이렉트로 돌아오면(같은 페이지가 팝업 안에서 다시 로드됨) URL에 담긴 값을
//   읽어 window.opener로 postMessage한 뒤 스스로 닫는 동일한 패턴 — 이 파일이 웹에서
//   로드될 때마다(모듈 스코프) "혹시 내가 그 팝업인가?"를 검사하므로 App.js 등 다른
//   파일을 건드릴 필요가 없음. 다만 두 provider가 돌려주는 값의 종류가 다름:
//   - 네이버: OAuth 암묵적 인가(response_type=token) — 팝업이 돌아올 때 URL **해시**
//     (#access_token=...)에 토큰이 바로 담겨 옴, 서버 교환 불필요.
//   - 카카오: 공식 JS SDK 문서상 "리다이렉트 기반 흐름이라 팝업만으로는 로그인이
//     불가능하며 서버 구현이 필요"함 — 실제로는 인가코드 방식(response_type=code)만
//     지원해서, 팝업이 돌아올 때 URL **쿼리스트링**(?code=...)에 인가코드만 담겨 오고,
//     이 코드를 access_token으로 바꾸는 건 kakaoLoginCF가 서버에서 대신 처리함(REST API
//     키가 필요해 클라 코드에 노출 안 함).
//   ⚠️ 둘 다 콘솔 설정이 선행돼야 실제로 동작함(코드만으로는 안 됨):
//   - 카카오: 앱 설정 > 카카오 로그인 > Redirect URI에 이 앱의 웹 도메인(예:
//     https://unlockapp.co.kr/) 등록 + 플랫폼 > Web에 동일 도메인 등록.
//   - 네이버: 애플리케이션 정보 > PC웹 플랫폼에 동일한 콜백 URL 등록(끝 슬래시 등
//     한 글자도 다르면 실패).

import { Platform } from 'react-native';
import { signInWithCustomToken, GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { login as kakaoLogin } from '@react-native-seoul/kakao-login';
import NaverLogin from '@react-native-seoul/naver-login';
import { auth, functionsAsia } from './firebase';

// ─── access token → Firebase 로그인 공통 처리 ─────────────────
// 네이티브/웹 어느 경로로 얻은 토큰이든 이 두 함수만 거치면 됨 — CF는 토큰 출처를
// 구분하지 않으므로 중복 없이 재사용.
// payload: {accessToken}(네이티브) 또는 {code, redirectUri}(웹) — kakaoLoginCF가 둘 다 받음
const exchangeKakaoToken = async (payload) => {
  const cf = httpsCallable(functionsAsia, 'kakaoLoginCF');
  const { data } = await cf(payload);
  if (!data?.firebaseToken) throw new Error('카카오 인증 토큰 발급 실패');
  const { user } = await signInWithCustomToken(auth, data.firebaseToken);
  const p = data.profile || {};
  return {
    uid: user.uid, email: p.email || '', displayName: p.displayName || '',
    photoURL: p.photoURL || '', provider: 'kakao',
  };
};

const exchangeNaverToken = async (accessToken) => {
  const cf = httpsCallable(functionsAsia, 'naverLoginCF');
  const { data } = await cf({ accessToken });
  if (!data?.firebaseToken) throw new Error('네이버 인증 토큰 발급 실패');
  const { user } = await signInWithCustomToken(auth, data.firebaseToken);
  const p = data.profile || {};
  return {
    uid: user.uid, email: p.email || '', displayName: p.displayName || '',
    photoURL: p.photoURL || '', provider: 'naver',
  };
};

// ─── 네이버 SDK 초기화 (네이티브 전용) ─────────────────────────
// 앱 시작 시 1회 호출되어야 함(App.js 또는 LoginScreen mount).
// 멱등 처리를 위해 내부 플래그로 중복 init 방지.
let _naverInitialized = false;
export const initNaver = () => {
  if (Platform.OS === 'web') return; // 웹은 아래 별도 팝업 플로우를 씀, 네이티브 SDK 초기화 불필요
  if (_naverInitialized) return;
  NaverLogin.initialize({
    appName:      process.env.EXPO_PUBLIC_NAVER_APP_NAME || 'UNLOCK',
    consumerKey:  process.env.EXPO_PUBLIC_NAVER_CLIENT_ID,
    consumerSecret: process.env.EXPO_PUBLIC_NAVER_CLIENT_SECRET,
    serviceUrlSchemeIOS: process.env.EXPO_PUBLIC_NAVER_URL_SCHEME,
    disableNaverAppAuthIOS: false,
  });
  _naverInitialized = true;
};

// ─── 카카오 로그인 (네이티브) ──────────────────────────────────
// 반환: { uid, email, displayName, photoURL, provider } — 인증만 수행, 프로필 생성은
// 호출부(LoginScreen.js)가 신규/기존 판별 후 처리
export const signInWithKakao = async () => {
  const kakaoToken = await kakaoLogin();
  const accessToken = kakaoToken?.accessToken;
  if (!accessToken) throw new Error('카카오 토큰을 받지 못했습니다.');
  return exchangeKakaoToken({ accessToken });
};

// ─── 네이버 로그인 (네이티브) ──────────────────────────────────
export const signInWithNaver = async () => {
  initNaver();
  const result = await NaverLogin.login();
  const accessToken = result?.successResponse?.accessToken;
  if (!accessToken) {
    // 사용자가 취소한 경우 failureResponse 가 옴
    if (result?.failureResponse) {
      const err = new Error('NAVER_CANCELLED');
      err.code = 'NAVER_CANCELLED';
      throw err;
    }
    throw new Error('네이버 토큰을 받지 못했습니다.');
  }
  return exchangeNaverToken(accessToken);
};

// ─── Google 로그인 (Android 전용 네이티브 SDK) ───────────────
// 80차 후속: expo-auth-session(브라우저 리다이렉트)이 Android 타입 OAuth 클라이언트와
// 근본적으로 호환 안 돼 실패하던 문제 — 네이티브 SDK로 전환. iOS는 기존 LoginScreen.js의
// expo-auth-session 플로우를 그대로 씀(정상 동작 중이라 안 건드림).
// 오늘 fork 재검토로 발견/수정: @react-native-google-signin/google-signin은(카카오/네이버
// 라이브러리와 달리) import 시점에 TurboModuleRegistry.getEnforcing()을 즉시 호출해서,
// 최상단 정적 import만으로도 Expo Go에서 앱이 시작하자마자(버튼을 누르기도 전에) 전체
// 크래시가 났음(에뮬레이터+시뮬레이터로 재현 확인) — 실제로 이 함수가 호출되는 순간까지
// require를 미루면 Expo Go에서도 다른 모든 화면은 정상 렌더링되고, 이 버튼을 눌렀을 때만
// (카카오/네이버와 동일하게) 실패함.
let _googleConfigured = false;
export const signInWithGoogleNative = async () => {
  const { GoogleSignin } = require('@react-native-google-signin/google-signin');
  if (!_googleConfigured) {
    GoogleSignin.configure({ webClientId: process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID });
    _googleConfigured = true;
  }
  await GoogleSignin.hasPlayServices();
  const result = await GoogleSignin.signIn();
  if (result?.type === 'cancelled') {
    const err = new Error('GOOGLE_CANCELLED');
    err.code = 'GOOGLE_CANCELLED';
    throw err;
  }
  const idToken = result?.data?.idToken;
  if (!idToken) throw new Error('구글 토큰을 받지 못했습니다.');

  const credential = GoogleAuthProvider.credential(idToken);
  const { user } = await signInWithCredential(auth, credential);

  return {
    uid: user.uid, email: user.email || '', displayName: user.displayName || '',
    photoURL: user.photoURL || '', provider: 'google',
  };
};

// ═══════════════════════════════════════════════════════════════
// 웹 전용 — 카카오/네이버 (80차 계속)
// ═══════════════════════════════════════════════════════════════

// ─── 팝업 OAuth 공용 헬퍼 ───────────────────────────────────
// 카카오/네이버 웹 로그인 둘 다 "팝업을 열고 → 리다이렉트로 돌아온 팝업이 postMessage로
// 결과를 보내고 → 스스로 닫힘"을 기다리는 동일한 모양이라 한 곳에서 처리.
const getOAuthRedirectUri = () => `${window.location.origin}${window.location.pathname}`;

const waitForOAuthPopup = (authUrl, { popupName, successType, errorType, cancelCode, onSuccess }) =>
  new Promise((resolve, reject) => {
    const popup = window.open(authUrl, popupName, 'width=460,height=600');
    if (!popup) { reject(new Error('팝업이 차단됐어요. 브라우저의 팝업 차단을 해제해주세요.')); return; }
    let settled = false;
    const cleanup = () => { window.removeEventListener('message', onMessage); clearInterval(pollTimer); };
    const cancel = () => { const err = new Error(cancelCode); err.code = cancelCode; reject(err); };
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === successType) { settled = true; cleanup(); onSuccess(event.data).then(resolve, reject); }
      else if (event.data?.type === errorType) { settled = true; cleanup(); cancel(); }
    };
    window.addEventListener('message', onMessage);
    // 사용자가 팝업을 그냥 닫아버린 경우(메시지 없이) 감지
    const pollTimer = setInterval(() => { if (popup.closed && !settled) { cleanup(); cancel(); } }, 500);
  });

// 이 모듈이 웹에서 로드될 때마다(=아무 페이지나 열릴 때마다) "혹시 내가 카카오/네이버
// 로그인 팝업이 리다이렉트로 돌아온 상태인가?"를 확인 — 맞으면 부모 창에 결과를 전달하고
// 스스로 닫는다. 일반적인 페이지 로드에서는 두 조건 다 안 맞아 즉시 아무 일도 안 함.
const OAUTH_STATE_KEY = 'unlock_oauth_state';
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  try {
    if (window.opener) {
      const savedState = window.sessionStorage.getItem(OAUTH_STATE_KEY);
      const search = new URLSearchParams(window.location.search || '');
      const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
      const code = search.get('code');           // 카카오: 인가코드(쿼리스트링)
      const naverToken = hash.get('access_token'); // 네이버: 토큰(해시)
      const naverState = hash.get('state');

      if (code) {
        window.opener.postMessage({ type: 'UNLOCK_KAKAO_LOGIN', code }, window.location.origin);
        window.close();
      } else if (naverToken && naverState && savedState && naverState === savedState) {
        window.opener.postMessage({ type: 'UNLOCK_NAVER_LOGIN', accessToken: naverToken }, window.location.origin);
        window.close();
      } else if (search.get('error') || hash.get('error')) {
        const type = search.has('error') ? 'UNLOCK_KAKAO_LOGIN_ERROR' : 'UNLOCK_NAVER_LOGIN_ERROR';
        window.opener.postMessage({ type }, window.location.origin);
        window.close();
      }
    }
  } catch (_) { /* 팝업이 아니거나 파싱 실패 — 조용히 무시, 일반 페이지 로드는 계속 진행 */ }
}

// ─── 카카오 웹 ──────────────────────────────────────────────
// 공식 JS SDK 문서상 팝업만으로는 로그인을 완결할 수 없어(인가코드 방식만 지원, 서버
// 교환 필요) — JS SDK 자체를 로드하지 않고 인가 URL을 직접 구성, 돌아온 인가코드를
// kakaoLoginCF에 그대로 전달해 서버에서 토큰 교환까지 처리.
export const signInWithKakaoWeb = () => {
  if (Platform.OS !== 'web') return Promise.reject(new Error('web 전용 함수입니다.'));
  const restApiKey = process.env.EXPO_PUBLIC_KAKAO_REST_API_KEY;
  if (!restApiKey) return Promise.reject(new Error('EXPO_PUBLIC_KAKAO_REST_API_KEY가 설정되지 않았습니다.'));
  const redirectUri = getOAuthRedirectUri();
  const authUrl = `https://kauth.kakao.com/oauth/authorize?response_type=code`
    + `&client_id=${encodeURIComponent(restApiKey)}`
    + `&redirect_uri=${encodeURIComponent(redirectUri)}`;
  return waitForOAuthPopup(authUrl, {
    popupName: 'kakaoLogin',
    successType: 'UNLOCK_KAKAO_LOGIN',
    errorType: 'UNLOCK_KAKAO_LOGIN_ERROR',
    cancelCode: 'KAKAO_CANCELLED',
    onSuccess: (data) => exchangeKakaoToken({ code: data.code, redirectUri }),
  });
};

// ─── 네이버 웹 ──────────────────────────────────────────────
// 네이버 공식 JS SDK는 "네이버 로그인" 버튼을 자기가 직접 그리는 위젯 방식이라 이 앱의
// 커스텀 버튼 UI에 맞지 않음 — 대신 OAuth 2.0 암묵적 인가(response_type=token) 방식으로
// 팝업을 직접 열고, 위 공용 콜백 감지 로직이 돌아온 토큰을 opener로 전달해줌.
// ⚠️ redirect_uri로 쓰는 이 페이지 주소가 네이버 개발자센터의 "PC웹" 플랫폼에 콜백
// URL로 정확히 등록돼 있어야 함(끝 슬래시 등 한 글자도 다르면 실패).
export const signInWithNaverWeb = () => {
  if (Platform.OS !== 'web') return Promise.reject(new Error('web 전용 함수입니다.'));
  const clientId = process.env.EXPO_PUBLIC_NAVER_CLIENT_ID;
  if (!clientId) return Promise.reject(new Error('EXPO_PUBLIC_NAVER_CLIENT_ID가 설정되지 않았습니다.'));
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
  window.sessionStorage.setItem(OAUTH_STATE_KEY, state);
  const authUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=token`
    + `&client_id=${encodeURIComponent(clientId)}`
    + `&redirect_uri=${encodeURIComponent(getOAuthRedirectUri())}`
    + `&state=${encodeURIComponent(state)}`;
  return waitForOAuthPopup(authUrl, {
    popupName: 'naverLogin',
    successType: 'UNLOCK_NAVER_LOGIN',
    errorType: 'UNLOCK_NAVER_LOGIN_ERROR',
    cancelCode: 'NAVER_CANCELLED',
    onSuccess: (data) => exchangeNaverToken(data.accessToken),
  });
};
